import type { AccessSession } from "@shared/access.types";
import { useEffect, useState } from "react";
import { Eyebrow } from "@/components/ds/Eyebrow";
import { PageMessage } from "@/components/PageMessage";
import { api } from "@/lib/api";
import { copy } from "@/lib/copy";

/** Provisoire : prouve que la session tient. Les vrais écrans viendront des maquettes. */

/**
 * ⚠️ Trois états, pas deux. La version précédente en avait deux, « en cours » et
 * « vérifié », et posait `.then()` sans `.catch()` : une coupure réseau ou un délai
 * dépassé laissait « en cours » POUR TOUJOURS, donc trois petits points au milieu d'un
 * écran vide, sans message ni bouton. `copy.share.failed` existait déjà pour ce cas, et
 * n'était appelé de nulle part.
 *
 * « Pas de session » et « je ne sais pas » ne se confondent pas non plus : envoyer
 * chercher un vieux mail (« ouvrez le lien reçu par mail ») une entreprise dont l'accès
 * est valide et dont le réseau a lâché, c'est la faire chercher un problème qui n'existe
 * pas.
 */
type Etat =
	| { statut: "verification" }
	| { statut: "echec" }
	| { statut: "sans-acces" }
	| { statut: "ouvert"; session: AccessSession };

export function SharePage() {
	const [etat, setEtat] = useState<Etat>({ statut: "verification" });

	useEffect(() => {
		api
			.currentSession()
			.then((session) => {
				setEtat(session ? { statut: "ouvert", session } : { statut: "sans-acces" });
			})
			.catch(() => {
				setEtat({ statut: "echec" });
			});
	}, []);

	if (etat.statut === "verification") {
		return (
			<main className="grid min-h-screen place-items-center px-5 text-mg-secondary">
				<p className="t-body">{copy.share.checking}</p>
			</main>
		);
	}

	if (etat.statut !== "ouvert") {
		const sansAcces = etat.statut === "sans-acces";
		/*
		 * Le bouton n'apparaît QUE sur l'échec. « Rechargez la page pour réessayer »
		 * sans rien à cliquer laissait le geste à faire à la main, alors que les deux
		 * autres écrans corrigés le même jour le proposent. Sur « aucun accès ouvert »,
		 * recharger ne changerait rien : c'est le lien reçu par mail qu'il faut rouvrir.
		 */
		return (
			<PageMessage
				title={sansAcces ? copy.share.noAccessTitle : copy.share.title}
				body={sansAcces ? copy.share.noAccessBody : copy.share.failed}
				retry={
					sansAcces
						? undefined
						: { label: copy.deposit.retry, onClick: () => window.location.reload() }
				}
			/>
		);
	}

	return (
		<main className="mx-auto w-full max-w-2xl px-5 py-16">
			<Eyebrow>{copy.share.eyebrow}</Eyebrow>
			<h1 className="mt-2 t-h1 text-mg-heading">{etat.session.companyName}</h1>
			<p className="t-body mt-2 text-mg-secondary">
				{copy.share.sharedBy(etat.session.schoolName)}
			</p>
		</main>
	);
}
